// src/pages/IngressoPage.tsx — ingresso individual, mobile-first.
// Rota /ingresso/:ticketNumber. Exige sessão do portal (email + código OTP em
// localStorage); busca o ticket em POST /meus/ingressos e exibe QR grande +
// todas as infos do PDF. Sem sessão válida, volta para /meus-ingressos.
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { EVENTOS, getEventoAtual } from '../config/event';
import IngressoCard from '../components/IngressoCard';
import './LandingPage.css';
import './IngressoPage.css';

const API_BASE = 'https://ta3m3foyci.execute-api.sa-east-1.amazonaws.com';

interface Ingresso {
  ticket_number: number;
  nome: string;
  documento: string;
  email: string;
  tipo_ingresso: string;
  documento_meia_entrada?: string | null;
  payment_id?: string;
  payment_approved: boolean;
  evento_id?: string;
}

const IngressoPage: React.FC = () => {
  const { ticketNumber } = useParams<{ ticketNumber: string }>();
  const [ingresso, setIngresso] = useState<Ingresso | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const email = localStorage.getItem('era_meus_email') || '';
    const codigo = localStorage.getItem('era_meus_code') || '';
    if (!email || !codigo) {
      setError('no-session');
      setIsLoading(false);
      return;
    }
    fetch(`${API_BASE}/meus/ingressos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, codigo }),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.message || 'Erro ao buscar ingresso.');
        const lista: Ingresso[] = Array.isArray(data) ? data : [];
        const achado = lista.find((i) => String(i.ticket_number) === String(ticketNumber));
        if (!achado) throw new Error('not-found');
        if (!achado.payment_approved) throw new Error('not-paid');
        setIngresso(achado);
      })
      .catch((err: any) => setError(err?.message || 'Erro ao buscar ingresso.'))
      .finally(() => setIsLoading(false));
  }, [ticketNumber]);

  const evento = ingresso?.evento_id && EVENTOS[ingresso.evento_id]
    ? EVENTOS[ingresso.evento_id]
    : getEventoAtual();

  return (
    <div className="era-ingresso">
      <header className="era-ingresso-top">
        <Link to="/meus-ingressos" className="era-ingresso-voltar">‹ Meus ingressos</Link>
        <p className="era-ingresso-marca">ESPAÇO ERA</p>
      </header>

      <main className="era-ingresso-main">
        {isLoading && <p className="era-ingresso-status" role="status">Carregando ingresso…</p>}

        {!isLoading && (error === 'no-session' || error === 'not-found') && (
          <div className="era-ingresso-card">
            <h1 className="era-ingresso-titulo">Ingresso não encontrado</h1>
            <p className="era-ingresso-texto">
              Entre com seu email e o código de acesso para ver seus ingressos.
            </p>
            <Link to="/meus-ingressos" className="era-primary-btn">Ver meus ingressos</Link>
          </div>
        )}

        {!isLoading && error === 'not-paid' && (
          <div className="era-ingresso-card">
            <h1 className="era-ingresso-titulo">Pagamento pendente</h1>
            <p className="era-ingresso-texto">
              O QR deste ingresso aparece após a confirmação do pagamento.
            </p>
            <Link to="/meus-ingressos" className="era-primary-btn">Ver meus ingressos</Link>
          </div>
        )}

        {!isLoading && error && !['no-session', 'not-found', 'not-paid'].includes(error) && (
          <div className="era-ingresso-card">
            <h1 className="era-ingresso-titulo">Erro ao buscar ingresso</h1>
            <p className="era-ingresso-texto">{error}</p>
            <Link to="/meus-ingressos" className="era-primary-btn">Ver meus ingressos</Link>
          </div>
        )}

        {!isLoading && ingresso && (
          <IngressoCard ingresso={ingresso} evento={evento} />
        )}
      </main>
    </div>
  );
};

export default IngressoPage;
